import { useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { mediaUrl } from "../mediaUrl";
import { usePageMeta } from "../usePageMeta";
import { ShareButton } from "../components/ShareButton";
import { StudioLink } from "../components/StudioLink";
import {
  PREVIEW_COLOUR_NOTE,
  PREVIEW_DESCRIPTION,
  PREVIEW_FAQ,
  PREVIEW_FORMATS,
  PREVIEW_HEADING,
  PREVIEW_INTRO,
  PREVIEW_PATH,
  PREVIEW_STEPS,
  PREVIEW_STRUCTURED_DATA,
  PREVIEW_TITLE,
} from "../previewCopy";

interface PreviewPageProps {
  apiUrl: string;
  formatConverterEnabled: boolean;
  stitchReplayerEnabled: boolean;
  shareButtonsEnabled: boolean;
  // The digitizing_service flag, resolved true with its config loaded (App's
  // digitizingServiceOn): the quote link under a result shows only then.
  digitizingServiceEnabled: boolean;
  onNavigateToDigitizingService: () => void;
}

interface PreviewResult {
  source_format: string;
  source_filename: string;
  width_inches: number;
  height_inches: number;
  size_warning: boolean;
  png: string;
  content_key: string;
}

// Kept in sync with app/utils/embroidery.py::READ_FORMATS -- the backend is
// the real gate, this is only so the file picker and an obviously wrong file
// fail fast instead of round-tripping to the server first.
const ACCEPTED_EXTENSIONS = [".pes", ".dst", ".exp", ".jef", ".vp3"];

// For viewer_file_loaded's error_type: what went wrong, never the file's name.
type ViewerError = "unsupported_format" | "unreadable" | "no_preview" | "network";

// This page already ran the upload through /api/convert to get its own
// preview -- carrying the result over the URL lets /convert show it
// immediately instead of asking for the same file a second time. Read by
// the matching effect in ConvertPage.tsx.
function convertLinkFor(result: PreviewResult): string {
  const params = new URLSearchParams({
    content_key: result.content_key,
    source_format: result.source_format,
    source_filename: result.source_filename,
    width_inches: String(result.width_inches),
    height_inches: String(result.height_inches),
    size_warning: result.size_warning ? "1" : "0",
  });
  return `/convert?${params.toString()}`;
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
}

/**
 * The fast path for "what's in this file" -- upload, see a static image,
 * done. Deliberately not the stitch simulator: that answers a different,
 * slower question ("how will this sew"), and someone who just wants to see
 * a design shouldn't have to sit through an animation to get there. Watch
 * it stitch and convert-to-another-format are both one click away from
 * here instead, not the default.
 *
 * Reuses /api/convert rather than a new endpoint -- it already returns a
 * PNG and a content_key as part of every conversion, this page just never
 * surfaces the format-conversion half of that response.
 *
 * Its words live in previewCopy.ts, which the server reads too.
 */
export function PreviewPage({
  apiUrl,
  formatConverterEnabled,
  stitchReplayerEnabled,
  shareButtonsEnabled,
  digitizingServiceEnabled,
  onNavigateToDigitizingService,
}: PreviewPageProps) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<PreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const posthog = usePostHog();

  usePageMeta({
    title: PREVIEW_TITLE,
    description: PREVIEW_DESCRIPTION,
    path: PREVIEW_PATH,
    structuredData: PREVIEW_STRUCTURED_DATA,
  });

  // format is the extension alone, lowercased and without the dot.
  const reportLoaded = (format: string, failure?: ViewerError) => {
    posthog?.capture("viewer_file_loaded", {
      format,
      success: !failure,
      ...(failure ? { error_type: failure } : {}),
    });
  };

  const handleFileChange = (selected: File | null) => {
    setError("");
    setResult(null);
    if (selected && !ACCEPTED_EXTENSIONS.includes(extensionOf(selected.name))) {
      setFile(null);
      setError(
        `Unsupported file type. Supported formats: ${ACCEPTED_EXTENSIONS.map((e) => e.slice(1)).join(", ").toUpperCase()}`
      );
      reportLoaded(extensionOf(selected.name).slice(1) || "none", "unsupported_format");
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError("Choose a file to preview");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    const format = extensionOf(file.name).slice(1);

    try {
      const formData = new FormData();
      formData.append("file", file);

      let response: Response;
      try {
        response = await fetch(`${apiUrl}/api/convert`, {
          method: "POST",
          body: formData,
        });
      } catch {
        reportLoaded(format, "network");
        throw new Error("Could not reach the server. Check your connection and try again.");
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        reportLoaded(format, "unreadable");
        throw new Error(errorData.detail || "Could not read this file");
      }

      const data = await response.json();
      if (!data.files?.png) {
        reportLoaded(format, "no_preview");
        throw new Error("Could not generate a preview for this file");
      }

      setResult({
        source_format: data.source_format,
        source_filename: data.source_filename,
        width_inches: data.width_inches,
        height_inches: data.height_inches,
        size_warning: data.size_warning,
        png: data.files.png,
        content_key: data.content_key,
      });
      posthog?.capture("embroidery_preview_uploaded", {
        source_format: data.source_format,
      });
      reportLoaded(format);
    } catch (err: any) {
      setError(err.message || "Could not read this file");
    } finally {
      setLoading(false);
    }
  };

  const resetFile = () => {
    setFile(null);
    setResult(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const nextStep = (target: "convert" | "simulator" | "digitizing") => {
    posthog?.capture("viewer_next_step_clicked", { target }, { transport: "sendBeacon" });
  };

  return (
    <>
      <section className="tool-intro">
        <h1>{PREVIEW_HEADING}</h1>
        <p>{PREVIEW_INTRO}</p>
      </section>

      <div className="two-column-layout">
        <div className="column-left">
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {/* The whole box is the file picker's label, so a click anywhere
                in it opens the picker, and a file dropped on it is handed to
                the same handler the picker uses. */}
            <label
              className={`file-dropzone${dragging ? " dragging" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                handleFileChange(e.dataTransfer.files?.[0] ?? null);
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="file-dropzone-input"
                accept={ACCEPTED_EXTENSIONS.join(",")}
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
              <span className="file-dropzone-prompt">
                {file ? file.name : "Drop your embroidery file here, or choose one"}
              </span>
              <small className="file-dropzone-formats">Supports DST, PES, JEF, EXP and VP3</small>
            </label>

            <button
              type="submit"
              aria-busy={loading}
              disabled={loading || !file}
              style={{ width: "100%", marginTop: "1.25rem" }}
            >
              {loading ? "Reading your file…" : "View Design"}
            </button>

            {file && (
              <button
                type="button"
                className="secondary outline"
                onClick={resetFile}
                disabled={loading}
                style={{ width: "100%", marginTop: "0.5rem" }}
              >
                Clear
              </button>
            )}
          </form>
        </div>

        <div className="column-right">
          <h2 className="column-heading">Preview</h2>

          {error && (
            <article className="error-message">
              <p><strong>Error:</strong> {error}</p>
            </article>
          )}

          {loading && (
            <div className="empty-preview">
              <p aria-busy="true">Reading your file…</p>
            </div>
          )}

          {!loading && !result && !error && (
            <div className="empty-preview">
              <p>Your design will appear here</p>
              <small>Upload a file on the left to view it</small>
            </div>
          )}

          {!loading && result && (
            <div className="result-preview">
              <figure className="preview-container">
                <img src={mediaUrl(apiUrl, result.png)} alt="Embroidery preview" />
                <figcaption style={{ marginTop: "1rem", fontSize: "0.9em", color: "var(--pico-muted-color)" }}>
                  {result.width_inches.toFixed(2)}" × {result.height_inches.toFixed(2)}"
                  {" · from "}{result.source_format.toUpperCase()}
                </figcaption>
              </figure>

              {/* Small links, not pop-ups: what to do with the file next. */}
              <ul className="viewer-next-steps">
                {formatConverterEnabled && (
                  <li>
                    <a href={convertLinkFor(result)} onClick={() => nextStep("convert")}>
                      Convert to another format →
                    </a>
                  </li>
                )}
                {stitchReplayerEnabled && (
                  <li>
                    <a href={`/replay/content/${result.content_key}`} onClick={() => nextStep("simulator")}>
                      Watch it stitch →
                    </a>
                    {shareButtonsEnabled && (
                      <>
                        {"  ·  "}
                        <ShareButton path={`/replay/content/${result.content_key}`} source="preview" />
                      </>
                    )}
                  </li>
                )}
                {digitizingServiceEnabled && (
                  <li>
                    <a
                      href="/digitizing-service?from=viewer"
                      onClick={(e) => {
                        e.preventDefault();
                        nextStep("digitizing");
                        onNavigateToDigitizingService();
                      }}
                    >
                      Need changes or a logo digitized? Get a quote →
                    </a>
                  </li>
                )}
                <li className="studio-upsell">
                  <StudioLink source="preview" newTab>
                    Edit this file in Stitch Studio, free{" "}→
                  </StudioLink>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <section className="tool-details">
        <h2>How to view an embroidery file online</h2>
        <ol>
          {PREVIEW_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>

        <h2>Supported embroidery file formats</h2>
        <ul>
          {PREVIEW_FORMATS.map((f) => (
            <li key={f.name}>
              <strong>{f.name}:</strong> {f.line}
            </li>
          ))}
        </ul>
        <p>{PREVIEW_COLOUR_NOTE}</p>
      </section>

      <section className="faq-section">
        <h2>Frequently asked questions</h2>
        <div className="faq-container">
          {PREVIEW_FAQ.map((faq, index) => (
            <div key={faq.question} className="faq-item">
              <button
                className="faq-question"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openFAQ === index}
                type="button"
              >
                <span>{faq.question}</span>
                <span className={`faq-icon ${openFAQ === index ? 'open' : ''}`}>
                  {openFAQ === index ? '−' : '+'}
                </span>
              </button>
              <div className={`faq-answer ${openFAQ === index ? 'open' : ''}`}>
                {faq.answer.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
